import Datepicker from 'components/ReusableComponents/Datepicker'
import DateIcon from 'components/ReusableComponents/Icons/Date'
import UserIcon from 'components/ReusableComponents/Icons/User'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import Toggle from 'components/ReusableComponents/Toggle'
import { hasMeaningfulCutoffDate } from 'features/CustomCampaigns/campaignAudienceDefaults'
import { getCampaignEstimation } from 'features/CustomCampaigns/EditCampaign/campaignEstimation'
import { getGuestsToReach } from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import useCampaignCredits from 'hooks/useCampaignCredits'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { formatNumberAsThousands } from 'utils/priceHelpers'
import { isPromotionCodeValid } from 'utils/promotionHelpers'

import styles from './PromoteLaunchStep.module.scss'
import PromotionFields from './PromotionFields'

function PromoteLaunchStep({
  preview,
  audiences,
  audienceId,
  setAudienceId,
  isAudienceCalculating,
  isEditable,
  medium,
  deliveryStartDate,
  setDeliveryStartDate,
  deliveryFinishDate,
  setDeliveryFinishDate,
  promotionCode,
  setPromotionCode,
  minNightsToStay,
  setMinNightsToStay,
  maxDiscountPercentage,
  setMaxDiscountPercentage,
  promoTagline,
  setPromoTagline,
  hasPromoValidityDate,
  setHasPromoValidityDate,
  promoValidityStartDate,
  setPromoValidityStartDate,
  promoValidityEndDate,
  setPromoValidityEndDate
}) {
  const { credits } = useCampaignCredits()

  const [isUsingPromotion, setIsUsingPromotion] = useState(Boolean(promotionCode))
  const [showCutOffDate, setShowCutOffDate] = useState(
    hasMeaningfulCutoffDate(deliveryFinishDate)
  )

  const isPromoValid = isUsingPromotion ? isPromotionCodeValid(promotionCode) : true

  useEffect(() => {
    setShowCutOffDate(hasMeaningfulCutoffDate(deliveryFinishDate))
  }, [deliveryFinishDate])

  const selectedAudience = audiences.find(audience => audience.id === Number(audienceId))
  const {
    total: totalGuestsToReach,
    toText: guestsToText,
    toEmail: guestsToEmail
  } = getGuestsToReach(selectedAudience, medium)

  const { guestsWillBeReached, estimatedEndDate } = getCampaignEstimation({
    deliveryStartDate,
    deliveryFinishDate,
    guestsToText,
    guestsToEmail,
    totalGuestsToReach,
    credits
  })

  function handleChangeCutOffToggle(event) {
    if (event.target.checked) {
      setShowCutOffDate(true)
    } else {
      setShowCutOffDate(false)
      setDeliveryFinishDate(null)
    }
  }

  return (
    <div className={styles.step}>
      <div className={styles.formColumn}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Promotion</h2>
          <p className={styles.sectionIntro}>Personalised discount for every channel</p>
        </section>

        <PromotionFields
          promotionCode={promotionCode}
          setPromotionCode={setPromotionCode}
          minNightsToStay={minNightsToStay}
          setMinNightsToStay={setMinNightsToStay}
          maxDiscountPercentage={maxDiscountPercentage}
          setMaxDiscountPercentage={setMaxDiscountPercentage}
          promoTagline={promoTagline}
          setPromoTagline={setPromoTagline}
          hasPromoValidityDate={hasPromoValidityDate}
          setHasPromoValidityDate={setHasPromoValidityDate}
          promoValidityStartDate={promoValidityStartDate}
          setPromoValidityStartDate={setPromoValidityStartDate}
          promoValidityEndDate={promoValidityEndDate}
          setPromoValidityEndDate={setPromoValidityEndDate}
          isUsingPromotion={isUsingPromotion}
          setIsUsingPromotion={setIsUsingPromotion}
          isPromoValid={isPromoValid}
        />

        <section className={styles.sectionDivided}>
          <h2 className={styles.sectionTitle}>Select your audience</h2>
          <p className={styles.sectionIntro}>Choose who will receive this campaign.</p>
        </section>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.fieldLabel}>Audience</span>
              <span className={styles.fieldHint}>
                {isAudienceCalculating
                  ? 'Calculating…'
                  : `Approx. ${formatNumberAsThousands(totalGuestsToReach)} guests`}
              </span>
            </div>
            <Dropdown
              className={styles.fullWidthInput}
              value={audienceId}
              disabled={!isEditable}
              options={audiences
                .filter(audience => !audience.isDeleted)
                .reduce((prev, curr) => ({ ...prev, [curr.id]: curr.name }), {})}
              onChange={event => setAudienceId(event.target.value)}
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Start sending on</span>
            <Datepicker
              disabled={!isEditable}
              className={styles.datepicker}
              placeholderText="Select date"
              selected={deliveryStartDate ? moment(deliveryStartDate).toDate() : null}
              minDate={moment().toDate()}
              onChange={date =>
                setDeliveryStartDate(moment(date).startOf('day').format())
              }
            />
          </div>
        </div>
        {moment(deliveryStartDate).isBefore(moment().startOf('day')) && (
          <p className={styles.fieldError}>Start date cannot be in the past</p>
        )}

        <div className={styles.toggleRow}>
          <div className={styles.toggleCopy}>
            <p className={styles.toggleTitle}>Add cut-off date</p>
            <p className={styles.toggleDescription}>
              Stop sending automatically at a certain date.
            </p>
          </div>
          <Toggle checked={showCutOffDate} handleChange={handleChangeCutOffToggle} />
        </div>
        {showCutOffDate && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Cut-off date</span>
            <Datepicker
              disabled={!isEditable}
              className={styles.datepicker}
              placeholderText="Select date"
              selected={
                hasMeaningfulCutoffDate(deliveryFinishDate)
                  ? moment(deliveryFinishDate).toDate()
                  : null
              }
              minDate={
                deliveryStartDate ? moment(deliveryStartDate).toDate() : moment().toDate()
              }
              onChange={date => setDeliveryFinishDate(moment(date).endOf('day').format())}
            />
            {hasMeaningfulCutoffDate(deliveryFinishDate) &&
              moment(deliveryFinishDate).isBefore(deliveryStartDate) && (
                <p className={styles.fieldError}>
                  Cut-off date should be after start date
                </p>
              )}
          </div>
        )}

        {!isAudienceCalculating && (
          <div className={styles.estimationCard}>
            <div className={styles.estimationRow}>
              <DateIcon width={16} height={16} fill="#0f8a5f" />
              <div>
                <p className={styles.estimationLabel}>Estimated end date</p>
                <p className={styles.estimationValue}>
                  {estimatedEndDate.format('MMM D, YYYY')}
                </p>
              </div>
            </div>
            <div className={styles.estimationRow}>
              <UserIcon width={14} height={14} fill="#0f8a5f" />
              <p className={styles.estimationDetail}>
                {formatNumberAsThousands(guestsWillBeReached)} guests will be reached
              </p>
            </div>
          </div>
        )}
      </div>

      <aside className={styles.previewColumn}>
        <span className={styles.previewLabel}>
          <i className={styles.previewDot} aria-hidden="true" />
          Live preview
        </span>
        <div className={styles.previewBody}>{preview}</div>
      </aside>
    </div>
  )
}

export default PromoteLaunchStep
